import { computeApplicability } from './jurisdiction-applicability-service.js';
import { getProfile as getReportingProfile } from './reporting-profile-service.js';
import { loadScopeInputs, narrowForReportingProfile, selectRulesFor } from './system-applicability-service.js';
import { attachLegalStanding, loadRuleChoices } from './workspace-rule-applicability-service.js';
import { ANSWER_FIELDS, SYSTEM_ANSWER_FIELDS, answersInBody, defaultFields, effectiveScope, overridesOf, withDerivedCategories, withoutAnswers, SCOPE_ANSWERS } from '../scope/scope-model.js';
/** Profile column → the answer it is the default for. */
const DEFAULT_ANSWER = {
    geographies: 'markets', data_types: 'data_classes', affected_persons: 'affected_persons',
    decision_types: 'decision_types', industry_codes: 'sector', deployer_type: 'deployer_type', federal_flowdown: 'federal_flowdown',
};
const FIELD_ANSWER = Object.fromEntries([
    ...Object.entries(ANSWER_FIELDS).flatMap(([a, fs]) => fs.map((f) => [f, a])),
    ...Object.entries(SYSTEM_ANSWER_FIELDS).flatMap(([a, fs]) => fs.map((f) => [f, a])),
    ['data_categories', 'data_classes'], ['affected_parties', 'affected_persons'],
]);
/** The answers a rule depends on: markets (its jurisdiction) plus each answer its predicates read. */
export function ruleDependsOn(predicates) {
    const out = new Set(['markets']);
    const walk = (p) => {
        if (!p || typeof p !== 'object')
            return;
        for (const [op, v] of Object.entries(p)) {
            if (op === 'and' || op === 'or')
                (Array.isArray(v) ? v : []).forEach(walk);
            else if (op === 'not')
                walk(v);
            else if (v && typeof v === 'object' && typeof v.field === 'string') {
                const a = FIELD_ANSWER[v.field];
                if (a)
                    out.add(a);
            }
        }
    };
    walk(predicates);
    return [...out];
}
/** Rule predicates by id, filled from the rule lists this module evaluates (for depends_on). */
const predicatesById = new Map();
const ruleRef = (r) => ({
    rule_id: r.rule_id, statute: r.statute, citation: r.citation, jurisdiction: r.jurisdiction, depends_on: ruleDependsOn(predicatesById.get(r.rule_id)),
});
function applicable(classification, rules, asOf, ruleChoices = {}) {
    for (const r of rules)
        predicatesById.set(r.rule_id, r.predicates);
    const out = new Map();
    for (const r of computeApplicability(classification, rules, { as_of: asOf, rule_choices: ruleChoices }).applicable)
        out.set(r.rule_id, r);
    return out;
}
/** v11.7 / ADR-0111 §7: rules with their law's standing, and the system's workspace's choices for contested ones. */
async function rulesWithStanding(db, systemId, profile) {
    const rules = await attachLegalStanding(db, await selectRulesFor(db, systemId, profile));
    const ws = (await db.query(`SELECT workspace_id FROM ai_systems WHERE system_id = ?`, [systemId])).results[0];
    const choices = ws && rules.some((r) => r.legal_status || r.preemption_risk) ? await loadRuleChoices(db, ws.workspace_id) : {};
    return { rules, choices };
}
/** `proposed` holds only the defaults being changed (omitted = unchanged; null = not answered). */
export async function previewScopeChange(db, workspaceId, proposed) {
    const changed = [...new Set(Object.keys(proposed)
            .filter((k) => k in DEFAULT_ANSWER && proposed[k] !== undefined).map((k) => DEFAULT_ANSWER[k]))]
        .sort((a, b) => SCOPE_ANSWERS.indexOf(a) - SCOPE_ANSWERS.indexOf(b));
    const systems = (await db.query(`SELECT system_id, name, classification_json, reporting_profile_id FROM ai_systems
      WHERE workspace_id = ? AND classification_json IS NOT NULL ORDER BY name`, [workspaceId])).results;
    const asOf = new Date().toISOString();
    const out = [];
    for (const sys of systems) {
        let classification;
        try {
            classification = JSON.parse(sys.classification_json);
        }
        catch {
            continue;
        }
        const { defaults, overrides } = await loadScopeInputs(db, sys.system_id);
        // A row without a record reads as legacy (effectiveScope): only a federal answer it doesn't carry inherits.
        const carried = overridesOf(null, classification);
        const own = (a) => overrides ? overrides.includes(a)
            : !(a === 'deployer_type' || a === 'federal_flowdown') || carried.includes(a);
        const inherits = changed.filter((a) => !own(a));
        const setsOwn = changed.filter((a) => own(a));
        const next = { ...defaults, ...proposed };
        const needsAnswer = inherits.filter((a) => Object.keys(defaultFields(a, defaults)).length > 0 && Object.keys(defaultFields(a, next)).length === 0);
        let added = [];
        let removed = [];
        if (inherits.length) {
            const profile = sys.reporting_profile_id ? await getReportingProfile(db, sys.reporting_profile_id) : null;
            const { rules, choices } = await rulesWithStanding(db, sys.system_id, profile);
            const eff = (d) => narrowForReportingProfile(withDerivedCategories(effectiveScope(classification, d, overrides)), profile);
            const before = applicable(eff(defaults), rules, asOf, choices);
            // Owner D5: a system that loses its sector keeps it (waiting on a pick), so the change removes nothing for it.
            const after = applicable(eff(needsAnswer.includes('sector') ? { ...next, industry_codes: defaults.industry_codes } : next), rules, asOf, choices);
            added = [...after.values()].filter((r) => !before.has(r.rule_id)).map(ruleRef);
            removed = [...before.values()].filter((r) => !after.has(r.rule_id)).map(ruleRef);
        }
        out.push({ system_id: sys.system_id, name: sys.name, inherits, sets_own: setsOwn, added, removed, needs_answer: needsAnswer });
    }
    return {
        changed,
        systems: out,
        totals: {
            added: out.reduce((n, s) => n + s.added.length, 0),
            removed: out.reduce((n, s) => n + s.removed.length, 0),
            systems_changed: out.filter((s) => s.added.length || s.removed.length).length,
            systems_need_answer: out.filter((s) => s.needs_answer.length).length,
        },
    };
}
/**
 * ADR-0099 §5 (V112-3-10): preview one system's save — the classification PATCH body (answers it sets) and the
 * answers it hands back to the workspace — against the rules that apply now. Same path as the save; writes nothing.
 */
export async function previewSystemScopeChange(db, systemId, patch, reset = []) {
    const sys = (await db.query(`SELECT classification_json, reporting_profile_id FROM ai_systems WHERE system_id = ?`, [systemId])).results[0];
    if (!sys)
        return null;
    let classification = {};
    try {
        classification = sys.classification_json ? JSON.parse(sys.classification_json) : {};
    }
    catch { /* unreadable = empty */ }
    const { defaults, overrides } = await loadScopeInputs(db, systemId);
    const own = overridesOf(overrides, sys.classification_json ? classification : {});
    const setNow = answersInBody(patch);
    const nextOverrides = [...new Set([...own, ...setNow])].filter((a) => !reset.includes(a));
    const nextClassification = withoutAnswers({ ...classification, ...patch }, reset);
    const profile = sys.reporting_profile_id ? await getReportingProfile(db, sys.reporting_profile_id) : null;
    const { rules, choices } = await rulesWithStanding(db, systemId, profile);
    const asOf = new Date().toISOString();
    const eff = (c, o) => narrowForReportingProfile(withDerivedCategories(effectiveScope(c, defaults, o)), profile);
    const before = applicable(eff(classification, overrides), rules, asOf, choices);
    const after = applicable(eff(nextClassification, nextOverrides), rules, asOf, choices);
    return {
        system_id: systemId,
        added: [...after.values()].filter((r) => !before.has(r.rule_id)).map(ruleRef),
        removed: [...before.values()].filter((r) => !after.has(r.rule_id)).map(ruleRef),
        before_count: before.size,
        after_count: after.size,
        current: [...before.values()].map(ruleRef),
    };
}
//# sourceMappingURL=scope-preview-service.js.map